import { useEffect, useState } from "react";
import { api, money, numFmt } from "@/lib/api";
import {
    Activity, AlertTriangle, AlertOctagon, ArrowUpRight, Truck, Users,
    Wallet, Receipt, Clock, FileText, Building2, TrendingUp
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import HelpTip from "@/components/HelpTip";
import { KPI_HELP } from "@/lib/helpTexts";

const Kpi = ({ icon: Icon, label, value, sub, tone = "default", testId, help }) => {
    const tones = {
        default: "border-slate-200",
        accent: "border-l-4 border-l-red-600 border-slate-200",
        positive: "border-l-4 border-l-emerald-500 border-slate-200",
        warning: "border-l-4 border-l-amber-500 border-slate-200",
    };
    return (
        <div className={`fm-card p-4 ${tones[tone]}`} data-testid={testId} title={help || label}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                    {label}
                    {help && <HelpTip text={help} />}
                </span>
                <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
            </div>
            <div className="font-mono font-bold text-2xl tabular-nums text-slate-900 leading-tight">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
};

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/dashboard/executive");
            if (data) setData(data);
            else setError("La API devolvió una respuesta vacía. Posible problema de sesión o configuración del backend.");
        } catch (e) {
            console.warn("dashboard fetch failed", e);
            const status = e?.response?.status;
            const msg = e?.response?.data?.detail || e?.message || "Error desconocido";
            setError(`No se pudo cargar el dashboard${status ? ` (HTTP ${status})` : ""}: ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
                <div className="text-slate-500 text-sm font-mono">Cargando dashboard...</div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="fm-card border-l-4 border-l-red-500 p-6 max-w-2xl" data-testid="dashboard-error">
                <div className="flex items-start gap-3 mb-3">
                    <AlertOctagon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h2 className="font-display font-bold text-lg">No se pudo cargar el dashboard</h2>
                        <p className="text-sm text-slate-600 mt-1 break-words">{error}</p>
                    </div>
                </div>
                <div className="text-[11px] text-slate-500 bg-slate-50 px-3 py-2 rounded-sm mb-3 font-mono">
                    URL backend: {api.defaults.baseURL || "/api"}
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="fm-btn-primary" data-testid="dashboard-retry">Reintentar</button>
                    <button onClick={() => { window.location.href = "/login"; }} className="fm-btn-outline">Volver a login</button>
                </div>
            </div>
        );
    }
    if (!data) return <div className="p-6 text-slate-500 text-sm">Sin datos</div>;

    const { kpis, top_clients, daily_revenue, alerts, expiring_units, services_by_type, low_stock_items = [], low_stock_count = 0 } = data;

    return (
        <div className="space-y-5" data-testid="executive-dashboard">
            {/* KPI Row 1 - Operación */}
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Operación</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <Kpi icon={Activity} label="Servicios Activos" value={kpis.services_active} tone="accent" testId="kpi-active" help={KPI_HELP.services_active} />
                    <Kpi icon={Clock} label="Pendientes" value={kpis.services_pending} tone="warning" testId="kpi-pending" help={KPI_HELP.services_pending} />
                    <Kpi icon={Truck} label="Unidades Disponibles" value={`${kpis.units_available}/${kpis.units_total}`} tone="positive" testId="kpi-units-available" help={KPI_HELP.available_units} />
                    <Kpi icon={AlertOctagon} label="Fuera Servicio" value={kpis.units_out} testId="kpi-units-out" help={KPI_HELP.out_of_service} />
                    <Kpi icon={FileText} label="Hoy" value={kpis.services_today} testId="kpi-today" help="Servicios capturados hoy en cabina." />
                    <Kpi icon={Users} label="Personal Activo" value={kpis.personnel_active} testId="kpi-personnel" help="Paramédicos y operadores con check-in activo hoy." />
                </div>
            </div>

            {/* KPI Row 2 - Finanzas */}
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Finanzas</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Kpi icon={TrendingUp} label="Ingresos Hoy" value={money(kpis.revenue_today)} tone="positive" testId="kpi-revenue-today" help="Total ingresado hoy por servicios cobrados o facturados." />
                    <Kpi icon={TrendingUp} label="Ingresos del Mes" value={money(kpis.revenue_month)} testId="kpi-revenue-month" help={KPI_HELP.revenue_month} />
                    <Kpi icon={Receipt} label="Cuentas por Cobrar" value={money(kpis.accounts_receivable)} sub={kpis.accounts_receivable_overdue > 0 ? `${money(kpis.accounts_receivable_overdue)} vencido` : "Al corriente"} tone={kpis.accounts_receivable_overdue > 0 ? "accent" : "default"} testId="kpi-ar" help={KPI_HELP.accounts_receivable} />
                    <Kpi icon={Wallet} label="Cuentas por Pagar" value={money(kpis.accounts_payable)} testId="kpi-ap" help={KPI_HELP.accounts_payable} />
                </div>
            </div>

            {/* Main grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Daily revenue chart */}
                <div className="lg:col-span-2 fm-card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="font-display font-bold text-base tracking-tight">Ingresos · Últimos 14 días</h3>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Tendencia operativa</div>
                        </div>
                        <Link to="/reports" className="text-[11px] uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1">
                            Ver reportes <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={daily_revenue}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                            <Tooltip
                                contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 2, fontSize: 12 }}
                                labelStyle={{ color: "#fff", fontFamily: "IBM Plex Mono" }}
                                itemStyle={{ color: "#10b981" }}
                                formatter={(v) => money(v)}
                            />
                            <Line type="monotone" dataKey="total" stroke="#dc2626" strokeWidth={2} dot={{ fill: "#dc2626", r: 3 }} activeDot={{ r: 5 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Alerts */}
                <div className="fm-card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-base tracking-tight">Alertas activas</h3>
                        <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                    </div>
                    <div className="space-y-2">
                        {alerts.length === 0 ? (
                            <div className="text-xs text-slate-500 py-4 text-center">Sin alertas activas</div>
                        ) : (
                            alerts.map((a, i) => (
                                <div key={i} className={`flex items-start gap-2 p-2 border-l-2 ${a.type === "critical" ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50"}`}>
                                    <AlertOctagon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${a.type === "critical" ? "text-red-600" : "text-amber-600"}`} strokeWidth={1.5} />
                                    <span className="text-xs text-slate-800">{a.msg}</span>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-200">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Documentos por vencer (30 días)</div>
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                            {expiring_units.length === 0 ? (
                                <div className="text-xs text-slate-400">Todo al día</div>
                            ) : (
                                expiring_units.map((e, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs">
                                        <span className="font-mono text-slate-700">{e.unit}</span>
                                        <span className="text-slate-500">{e.doc}</span>
                                        <span className={`fm-badge ${e.days < 0 ? "fm-badge-critical" : e.days < 15 ? "fm-badge-warning" : "fm-badge-neutral"}`}>
                                            {e.days < 0 ? `${Math.abs(e.days)}d vencido` : `${e.days}d`}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-200" data-testid="low-stock-section">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                            Stock mínimo crítico
                            {low_stock_count > 0 && <span className="bg-red-100 text-red-700 px-1.5 rounded-sm font-mono text-[10px]">{low_stock_count}</span>}
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {low_stock_items.length === 0 ? (
                                <div className="text-xs text-slate-400">Botiquines completos · sin alertas</div>
                            ) : (
                                low_stock_items.map((it, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs gap-2">
                                        <span className="flex-1 truncate" title={it.item_name}>
                                            <span className="font-mono text-[10px] text-slate-500">{it.sku}</span> {it.item_name}
                                        </span>
                                        <span className="text-[10px] text-slate-500 truncate max-w-[120px]" title={it.warehouse}>{it.warehouse}</span>
                                        <span className={`fm-badge ${it.severity === "critico" ? "fm-badge-critical" : "fm-badge-warning"}`}>
                                            {it.qty}/{it.min_stock}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top clients */}
                <div className="fm-card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-base tracking-tight">Top clientes del mes</h3>
                        <Building2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    </div>
                    {top_clients.length === 0 ? (
                        <div className="text-xs text-slate-500 py-8 text-center">Sin datos · Carga datos demo en Configuración</div>
                    ) : (
                        <table className="w-full">
                            <tbody>
                                {top_clients.map((c, i) => (
                                    <tr key={i} className="border-b border-slate-100 last:border-0">
                                        <td className="py-2 font-mono text-[10px] text-slate-400 tabular-nums w-6">{String(i + 1).padStart(2, "0")}</td>
                                        <td className="py-2 text-sm text-slate-900 font-medium">{c.name}</td>
                                        <td className="py-2 text-right font-mono text-sm font-bold tabular-nums">{money(c.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Services by type */}
                <div className="fm-card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-base tracking-tight">Servicios por tipo · Mes</h3>
                        <FileText className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    </div>
                    {services_by_type.length === 0 ? (
                        <div className="text-xs text-slate-500 py-8 text-center">Sin servicios este mes</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={services_by_type}>
                                <XAxis dataKey="code" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                                <Tooltip contentStyle={{ background: "#0f172a", border: "none", borderRadius: 2, fontSize: 12 }} itemStyle={{ color: "#dc2626" }} />
                                <Bar dataKey="count" fill="#0f172a" radius={0} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
}
